import { useContext, useEffect, useState } from "react";
import { AppContext } from "../../App";
import { getInlineLoader } from "../../Helpers";
import AOS from "aos";
import "aos/dist/aos.css";
import { IoMdArrowDropright } from "react-icons/io";
import {
  IoShieldCheckmarkOutline,
  IoConstructOutline,
  IoFlashOutline
} from "react-icons/io5";
import {
  FaArrowRight,
  FaCalculator,
  FaCalendarAlt,
  FaCar,
  FaChartLine,
  FaCheckCircle,
  FaClock,
  FaDollarSign,
  FaFileInvoice,
  FaHospital,
  FaShieldAlt,
  FaSitemap,
  FaTools,
  FaUsers
} from "react-icons/fa";

import heroBanner from "../../assets/banner1.jpg";
import serviceBanner2 from "../../assets/banner2.jpg";
import serviceBanner3 from "../../assets/banner3.jpg";
import serviceBanner4 from "../../assets/banner4.png";
import serviceBanner5 from "../../assets/banner3.jpg";
import projectBanner2 from "../../assets/banner4.png";

const enterpriseFeatures = [
  {
    title: "Custom-Built Systems",
    description:
      "Every system is designed around your workflows, ensuring a perfect fit for your business operations.",
    icon: IoConstructOutline,
    accentClass: "enterprise-accent-secondary"
  },
  {
    title: "Efficient & Scalable",
    description:
      "Optimized for performance today and built to scale seamlessly as your business grows.",
    icon: IoFlashOutline,
    accentClass: "enterprise-accent-sunrise"
  },
  {
    title: "Secure & Reliable",
    description:
      "Engineered with enterprise-grade security practices to protect your data and ensure consistent uptime.",
    icon: IoShieldCheckmarkOutline,
    accentClass: "enterprise-accent-primary"
  }
];

const enterpriseServices = [
  {
    title: "Financial Management",
    description:
      "Track income, expenses, and financial performance with absolute clarity.",
    icon: FaDollarSign,
    cardClass: "enterprise-service-card--finance"
  },
  {
    title: "Accounting Systems",
    description:
      "Automate bookkeeping, reporting, and compliance processes effortlessly.",
    icon: FaCalculator,
    cardClass: "enterprise-service-card--accounting"
  },
  {
    title: "CRM Solutions",
    description:
      "Manage customers, leads, and interactions in one unified dashboard.",
    icon: FaUsers,
    cardClass: "enterprise-service-card--crm"
  },
  {
    title: "ERP Integration",
    description:
      "Integrate operations across departments into a single source of truth.",
    icon: FaSitemap,
    cardClass: "enterprise-service-card--erp"
  },
  {
    title: "Custom Business Tools",
    description:
      "Built specifically for your unique workflows and operational needs.",
    icon: FaTools,
    cardClass: "enterprise-service-card--tools"
  }
];

const pricingFeatures = [
  "Business process analysis & requirements",
  "UI/UX design & prototyping",
  "Core system development & integration",
  "Testing, QA & user training",
  "Deployment & launch support"
];

const sampleProjects = [
  {
    id: 1,
    title: "Hospital Management System",
    subtitle: "Healthcare",
    description:
      "A comprehensive digital platform for managing patient records, appointments, billing, and inventory across multiple departments.",
    icon: FaHospital,
    badgeClass: "enterprise-project-badge--healthcare",
    mediaClass: "enterprise-project-media--hospital",
    features: [
      { icon: FaUsers, label: "Patient Management" },
      { icon: FaCalendarAlt, label: "Appointment Scheduling" },
      { icon: FaFileInvoice, label: "Billing & Invoicing" },
      { icon: FaChartLine, label: "Analytics Dashboard" }
    ],
    stats: [
      { value: "50+", label: "Departments" },
      { value: "10K+", label: "Patients" },
      { value: "99.9%", label: "Uptime" }
    ]
  },
  {
    id: 2,
    title: "Garage Management System",
    subtitle: "Automotive",
    description:
      "End-to-end solution for auto repair shops to track vehicles, manage mechanics, inventory parts, and process customer payments.",
    icon: FaCar,
    badgeClass: "enterprise-project-badge--automotive",
    mediaClass: "enterprise-project-media--garage",
    features: [
      { icon: FaCar, label: "Vehicle Tracking" },
      { icon: FaUsers, label: "Mechanic Management" },
      { icon: FaFileInvoice, label: "Service Invoicing" },
      { icon: FaChartLine, label: "Business Analytics" }
    ],
    stats: [
      { value: "200+", label: "Vehicles/Month" },
      { value: "15+", label: "Mechanics" },
      { value: "40%", label: "Efficiency Gain" }
    ]
  }
];

export default function SystemsHero() {
  const { navTo } = useContext(AppContext);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) {
      return;
    }

    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: false,
      offset: 50
    });
    AOS.refresh();
  }, [ready]);

  if (!ready) {
    return (
      <div className="container mSupportLoading">
        {getInlineLoader()}
      </div>
    );
  }

  const goToContact = () => navTo({ item: "contact_us" });

  return (
    <>
      <div className="enterprise-page" style={{ marginTop: "var(--topBarHeight)" }}>
        <section className="enterprise-hero position-relative overflow-hidden">
          <div className="enterprise-pattern enterprise-pattern--hero"></div>
          <div className="enterprise-orb enterprise-orb--hero-top"></div>
          <div className="enterprise-orb enterprise-orb--hero-bottom"></div>

          <div className="container position-relative enterprise-container enterprise-container--hero">
            <div className="text-center enterprise-hero__content">
              <h1
                className="enterprise-hero__title"
                data-aos="fade-up"
                data-aos-delay="200"
              >
                Werenium
                <br />
                <span className="enterprise-gradient-text">
                  Systems Development
                </span>
              </h1>

              <p
                className="enterprise-hero__text"
                data-aos="fade-up"
                data-aos-delay="300"
              >
                Enterprise systems engineered for efficiency, control, and
                growth.
              </p>

              <div data-aos="fade-up" data-aos-delay="400">
                <button
                  type="button"
                  className="enterprise-button enterprise-button--hero"
                  onClick={goToContact}
                >
                  Start Your Project <IoMdArrowDropright size={20} />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="enterprise-section enterprise-section--features overflow-hidden">
          <div className="enterprise-pattern enterprise-pattern--light"></div>
          <div className="enterprise-orb enterprise-orb--features-top"></div>
          <div className="enterprise-orb enterprise-orb--features-bottom"></div>

          <div className="container position-relative enterprise-container">
            <div className="enterprise-section-heading">
              <h2 className="enterprise-section-title" data-aos="fade-up">
                Built for{" "}
                <span className="enterprise-section-title__accent">
                  Real Business
                </span>
                <br />
                Operations
              </h2>

              <p
                className="enterprise-section-text"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                We develop tailored systems including ERP, CRM, accounting, and
                financial platforms focused on efficiency, control, and
                scalability.
              </p>
            </div>

            <div className="row g-4 enterprise-feature-grid">
              {enterpriseFeatures.map((feature, index) => {
                const Icon = feature.icon;

                return (
                  <div
                    key={feature.title}
                    className="col-lg-4"
                    data-aos="fade-up"
                    data-aos-delay={200 + index * 100}
                  >
                    <article
                      className={`enterprise-feature-card ${feature.accentClass}`}
                    >
                      <div className="enterprise-feature-card__icon-shell">
                        <div className="enterprise-feature-card__icon-core">
                          <Icon />
                        </div>
                      </div>

                      <h3 className="enterprise-feature-card__title">
                        {feature.title}
                      </h3>

                      <p className="enterprise-feature-card__text">
                        {feature.description}
                      </p>

                      <div className="enterprise-feature-card__accent"></div>
                    </article>
                  </div>
                );
              })}
            </div>

            <div
              className="enterprise-section-cta"
              data-aos="fade-up"
              data-aos-delay="500"
            >
              <button
                type="button"
                className="enterprise-button enterprise-button--primary"
                onClick={goToContact}
              >
                Build Your System <IoMdArrowDropright size={20} />
              </button>
            </div>
          </div>
        </section>

        <section className="enterprise-section enterprise-section--services overflow-hidden">
          <div className="enterprise-ambient enterprise-ambient--services-top"></div>
          <div className="enterprise-ambient enterprise-ambient--services-bottom"></div>

          <div className="container position-relative enterprise-container">
            <div className="enterprise-section-heading">
              <h2 className="enterprise-section-title" data-aos="fade-up">
                Systems Engineered for
                <br />
                <span className="enterprise-section-title__accent">
                  Growth & Efficiency
                </span>
              </h2>

              <p
                className="enterprise-section-text"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                A variation of systems we build.
              </p>
            </div>

            <div className="row g-4 mb-0 justify-content-center">
              {enterpriseServices.map((service, index) => {
                const Icon = service.icon;

                return (
                  <div
                    key={service.title}
                    className="col-md-6 mt-3"
                    data-aos="fade-up"
                    data-aos-delay={index * 100}
                  >
                    <article
                      className={`enterprise-service-card ${service.cardClass}`}
                    >
                      <div className="enterprise-service-card__overlay"></div>

                      <div className="enterprise-service-card__icon">
                        <Icon />
                      </div>

                      <div className="enterprise-service-card__content">
                        <h3 className="enterprise-service-card__title">
                          {service.title}
                        </h3>
                        <p className="enterprise-service-card__text">
                          {service.description}
                        </p>
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        <section className="enterprise-section enterprise-section--pricing overflow-hidden">
          <div className="enterprise-pattern enterprise-pattern--light"></div>
          <div className="enterprise-orb enterprise-orb--pricing-top"></div>
          <div className="enterprise-orb enterprise-orb--pricing-bottom"></div>

          <div className="container position-relative enterprise-container">
            <div className="enterprise-section-heading">
              <h2 className="enterprise-section-title" data-aos="fade-up">
                Invest in{" "}
                <span className="enterprise-gradient-text">
                  Growth & Efficiency
                </span>
              </h2>

              <p
                className="enterprise-section-text"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Custom systems tailored to your operations. No hidden fees, no
                generic templates - just engineered solutions for your business.
              </p>
            </div>

            <div className="row justify-content-center" data-aos="fade-up" data-aos-delay="200">
              <div className="col-lg-10">
                <div className="enterprise-pricing-card">
                  <div className="enterprise-pricing-card__topline"></div>

                  <div className="row g-5 align-items-start">
                    <div className="col-lg-5">
                      <div className="enterprise-pricing-intro">
                        <div className="enterprise-pricing-eyebrow">
                          STARTING FROM
                        </div>

                        <div className="enterprise-pricing-value">
                          1,500,000{" "}
                          <span className="enterprise-pricing-currency">
                            TZS
                          </span>
                        </div>

                        <p className="enterprise-pricing-note">
                          Final pricing depends on system complexity,
                          integrations, and scale. Get a tailored quote for
                          your specific needs.
                        </p>
                      </div>

                      <div className="row g-3">
                        <div className="col-6">
                          <div className="enterprise-pricing-meta">
                            <div className="enterprise-pricing-meta__label enterprise-pricing-meta__label--secondary">
                              <FaClock size={14} />
                              <span>Timeline</span>
                            </div>
                            <div className="enterprise-pricing-meta__value">
                              4-12 Weeks
                            </div>
                          </div>
                        </div>

                        <div className="col-6">
                          <div className="enterprise-pricing-meta">
                            <div className="enterprise-pricing-meta__label enterprise-pricing-meta__label--primary">
                              <FaShieldAlt size={14} />
                              <span>Support</span>
                            </div>
                            <div className="enterprise-pricing-meta__value">
                              3 Months
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-lg-7">
                      <div className="enterprise-pricing-list-title">
                        What's Included
                      </div>

                      <div className="enterprise-pricing-list">
                        {pricingFeatures.map((feature) => (
                          <div
                            key={feature}
                            className="enterprise-pricing-list__item"
                          >
                            <FaCheckCircle className="enterprise-pricing-list__icon" />
                            <span>{feature}</span>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        className="enterprise-button enterprise-button--primary"
                        onClick={goToContact}
                      >
                        Get Your Quote <FaArrowRight size={18} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="enterprise-section enterprise-section--projects overflow-hidden">
          <div className="enterprise-pattern enterprise-pattern--light"></div>
          <div className="enterprise-orb enterprise-orb--projects-top"></div>
          <div className="enterprise-orb enterprise-orb--projects-bottom"></div>

          <div className="container position-relative enterprise-container">
            <div className="enterprise-section-heading">
              <h2 className="enterprise-section-title" data-aos="fade-up">
                Our <span className="enterprise-gradient-text">Works</span>
              </h2>

              <p
                className="enterprise-section-text"
                data-aos="fade-up"
                data-aos-delay="100"
              >
                Real systems built for real businesses. Explore how we have
                transformed operations across different industries.
              </p>
            </div>

            <div className="row g-5 mb-0">
              {sampleProjects.map((project, index) => {
                const ProjectIcon = project.icon;

                return (
                  <div
                    key={project.id}
                    className="col-lg-6"
                    data-aos="fade-up"
                    data-aos-delay={index * 150}
                  >
                    <article className="enterprise-project-card">
                      <div
                        className={`enterprise-project-card__media ${project.mediaClass}`}
                      >
                        <div className="enterprise-project-card__tag">
                          {project.subtitle}
                        </div>

                        <div
                          className={`enterprise-project-card__badge ${project.badgeClass}`}
                        >
                          <ProjectIcon />
                        </div>

                        <div className="enterprise-project-card__media-overlay"></div>
                      </div>

                      <div className="enterprise-project-card__content">
                        <h3 className="enterprise-project-card__title">
                          {project.title}
                        </h3>

                        <p className="enterprise-project-card__description">
                          {project.description}
                        </p>

                        <div className="row g-3 mb-4">
                          {project.features.map((feature) => {
                            const FeatureIcon = feature.icon;

                            return (
                              <div key={feature.label} className="col-6">
                                <div className="enterprise-project-feature">
                                  <span className="enterprise-project-feature__icon">
                                    <FeatureIcon />
                                  </span>
                                  <span className="enterprise-project-feature__label">
                                    {feature.label}
                                  </span>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="enterprise-project-card__stats">
                          {project.stats.map((stat) => (
                            <div
                              key={stat.label}
                              className="enterprise-project-card__stat"
                            >
                              <div className="enterprise-project-card__stat-value">
                                {stat.value}
                              </div>
                              <div className="enterprise-project-card__stat-label">
                                {stat.label}
                              </div>
                            </div>
                          ))}
                        </div>

                        <button
                          type="button"
                          className="enterprise-button enterprise-button--primary enterprise-button--project"
                          onClick={goToContact}
                        >
                          Start Similar Project <FaArrowRight size={16} />
                        </button>
                      </div>
                    </article>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      </div>

      <style>{`
        .enterprise-page {
          color: #1a1a2e;
        }

        .enterprise-container {
          z-index: 1;
          max-width: 1200px;
        }

        .enterprise-container--hero {
          max-width: 900px;
        }

        .enterprise-hero,
        .enterprise-section {
          position: relative;
        }

        .enterprise-hero {
          min-height: calc(100vh - var(--topBarHeight));
          display: flex;
          align-items: center;
          justify-content: center;
          background:
            linear-gradient(
              135deg,
              rgba(26, 26, 46, 0.92) 0%,
              rgba(10, 10, 12, 0.88) 100%
            ),
            url(${heroBanner});
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
          background-attachment: fixed;
        }

        .enterprise-hero__content {
          padding: 0 1rem;
        }

        .enterprise-hero__title {
          margin: 0 auto 1rem;
          max-width: 800px;
          color: #ffffff;
          font-size: clamp(3rem, 8vw, 4rem);
          line-height: 1.05;
          letter-spacing: -0.03em;
          font-weight: 800;
        }

        .enterprise-hero__text {
          margin: 0 auto 2.5rem;
          max-width: 580px;
          color: rgba(255, 255, 255, 0.85);
          font-size: clamp(1.05rem, 2vw, 1.25rem);
          line-height: 1.7;
          font-weight: 400;
        }

        .enterprise-gradient-text {
          background: linear-gradient(135deg, var(--secondaryColor) 0%, #ff9f43 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          color: transparent;
        }

        .enterprise-section {
          padding: 5rem 0;
        }

        .enterprise-section--features {
          min-height: 80vh;
          background: linear-gradient(180deg, #ffffff 0%, #f8f9fc 100%);
        }

        .enterprise-section--services {
          background: #ffffff;
        }

        .enterprise-section--pricing {
          min-height: 100vh;
          background: linear-gradient(180deg, #f8f9fc 0%, #ffffff 100%);
        }

        .enterprise-section--projects {
          min-height: 100vh;
          background: linear-gradient(45deg, #fcede3 0%, #d4d2d2 100%);
        }

        .enterprise-section-heading {
          margin: 0 auto 3.5rem;
          max-width: 650px;
          text-align: center;
        }

        .enterprise-section-title {
          margin: 0 0 1rem;
          color: #1a1a2e;
          font-size: clamp(2.5rem, 5vw, 3.5rem);
          line-height: 1.1;
          letter-spacing: -0.02em;
          font-weight: 800;
        }

        .enterprise-section-title__accent {
          color: var(--secondaryColor);
        }

        .enterprise-section-text {
          margin: 0 auto;
          color: #64748b;
          font-size: 1.15rem;
          line-height: 1.8;
        }

        .enterprise-pattern,
        .enterprise-orb,
        .enterprise-ambient {
          pointer-events: none;
          z-index: 0;
        }

        .enterprise-pattern {
          position: absolute;
          inset: 0;
        }

        .enterprise-pattern--hero {
          background-image: radial-gradient(
            circle at 1px 1px,
            rgba(244, 145, 30, 0.04) 1px,
            transparent 0
          );
          background-size: 50px 50px;
          opacity: 0.6;
        }

        .enterprise-pattern--light {
          background-image: radial-gradient(
            circle at 1px 1px,
            rgba(244, 145, 30, 0.06) 1px,
            transparent 0
          );
          background-size: 40px 40px;
          opacity: 0.5;
        }

        .enterprise-orb,
        .enterprise-ambient {
          position: absolute;
          border-radius: 50%;
        }

        .enterprise-orb--hero-top {
          top: 20%;
          right: 15%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(244, 145, 30, 0.12) 0%, transparent 70%);
          filter: blur(80px);
          animation: enterprise-float 18s ease-in-out infinite;
        }

        .enterprise-orb--hero-bottom {
          bottom: 15%;
          left: 10%;
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(58, 58, 58, 0.1) 0%, transparent 70%);
          filter: blur(70px);
          animation: enterprise-float 22s ease-in-out infinite reverse;
        }

        .enterprise-orb--features-top {
          top: 10%;
          right: 5%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(244, 145, 30, 0.08) 0%, transparent 70%);
          filter: blur(60px);
        }

        .enterprise-orb--features-bottom {
          bottom: 10%;
          left: 5%;
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(58, 58, 58, 0.05) 0%, transparent 70%);
          filter: blur(50px);
        }

        .enterprise-ambient--services-top {
          top: -10%;
          right: -5%;
          width: 500px;
          height: 500px;
          background: var(--secondaryColor);
          opacity: 0.05;
          filter: blur(100px);
        }

        .enterprise-ambient--services-bottom {
          bottom: -10%;
          left: -10%;
          width: 600px;
          height: 600px;
          background: var(--primaryColor);
          opacity: 0.05;
          filter: blur(120px);
        }

        .enterprise-orb--pricing-top {
          top: 10%;
          right: 5%;
          width: 400px;
          height: 400px;
          background: radial-gradient(circle, rgba(244, 145, 30, 0.08) 0%, transparent 70%);
          filter: blur(70px);
          animation: enterprise-float 20s ease-in-out infinite;
        }

        .enterprise-orb--pricing-bottom {
          bottom: 10%;
          left: 10%;
          width: 350px;
          height: 350px;
          background: radial-gradient(circle, rgba(58, 58, 58, 0.05) 0%, transparent 70%);
          filter: blur(60px);
          animation: enterprise-float 25s ease-in-out infinite reverse;
        }

        .enterprise-orb--projects-top {
          top: 5%;
          right: 10%;
          width: 500px;
          height: 500px;
          background: radial-gradient(circle, rgba(244, 145, 30, 0.08) 0%, transparent 70%);
          filter: blur(80px);
          animation: enterprise-float 20s ease-in-out infinite;
        }

        .enterprise-orb--projects-bottom {
          bottom: 10%;
          left: 5%;
          width: 450px;
          height: 450px;
          background: radial-gradient(circle, rgba(8, 174, 234, 0.08) 0%, transparent 70%);
          filter: blur(80px);
          animation: enterprise-float 25s ease-in-out infinite reverse;
        }

        .enterprise-button {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border: 0;
          border-radius: 999px;
          cursor: pointer;
          font-size: 1.05rem;
          font-weight: 600;
          transition:
            transform 0.35s cubic-bezier(0.4, 0, 0.2, 1),
            box-shadow 0.35s ease,
            background 0.35s ease,
            color 0.35s ease;
        }

        .enterprise-button:hover {
          transform: translateY(-4px);
        }

        .enterprise-button:active {
          transform: translateY(-1px);
        }

        .enterprise-button--hero,
        .enterprise-button--primary {
          background: var(--secondaryColor);
          color: #ffffff;
          box-shadow: 0 8px 30px rgba(244, 145, 30, 0.35);
        }

        .enterprise-button--hero {
          min-width: 220px;
          padding: 0.95rem 1.75rem;
        }

        .enterprise-button--primary {
          padding: 0.95rem 2rem;
        }

        .enterprise-button--hero:hover,
        .enterprise-button--primary:hover {
          box-shadow: 0 14px 40px rgba(244, 145, 30, 0.5);
        }

        .enterprise-button--project {
          padding: 0.75rem 1.5rem;
          font-size: 0.95rem;
          box-shadow: none;
        }

        .enterprise-button--project:hover {
          transform: translateX(4px);
          background: #e8820e;
          box-shadow: none;
        }

        .enterprise-button--project:active {
          transform: translateX(2px);
        }

        .enterprise-section-cta {
          margin-top: 3rem;
          display: flex;
          justify-content: center;
        }

        .enterprise-feature-grid {
          margin-bottom: 0;
        }

        .enterprise-feature-card {
          --enterprise-accent: var(--secondaryColor);
          position: relative;
          height: 100%;
          padding: 3rem;
          border-radius: 1.5rem;
          background: rgba(0, 0, 0, 0.15);
          border: 2px solid rgba(255, 255, 255, 0.75);
          box-shadow: 0 4px 7px rgba(0, 0, 0, 0.07);
          overflow: hidden;
          transition:
            transform 0.4s cubic-bezier(0.4, 0, 0.2, 1),
            box-shadow 0.4s ease,
            border-color 0.4s ease;
        }

        .enterprise-feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.08);
          border-color: rgba(244, 145, 30, 0.15);
        }

        .enterprise-feature-card__icon-shell {
          width: 72px;
          height: 72px;
          margin-bottom: 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          border: 1px solid rgba(244, 145, 30, 0.18);
          transition:
            transform 0.4s ease,
            box-shadow 0.4s ease;
        }

        .enterprise-accent-secondary {
          --enterprise-accent: var(--secondaryColor);
        }

        .enterprise-accent-sunrise {
          --enterprise-accent: #f4911e;
        }

        .enterprise-accent-secondary .enterprise-feature-card__icon-shell,
        .enterprise-accent-sunrise .enterprise-feature-card__icon-shell {
          background: linear-gradient(
            135deg,
            rgba(244, 145, 30, 0.15) 0%,
            rgba(244, 145, 30, 0.08) 100%
          );
        }

        .enterprise-accent-primary {
          --enterprise-accent: var(--primaryColor);
        }

        .enterprise-accent-primary .enterprise-feature-card__icon-shell {
          background: linear-gradient(
            135deg,
            rgba(58, 58, 58, 0.15) 0%,
            rgba(58, 58, 58, 0.08) 100%
          );
          border-color: rgba(58, 58, 58, 0.18);
        }

        .enterprise-feature-card:hover .enterprise-feature-card__icon-shell {
          transform: scale(1.08) rotate(-5deg);
          box-shadow: 0 12px 28px rgba(244, 145, 30, 0.18);
        }

        .enterprise-feature-card__icon-core {
          width: 58px;
          height: 58px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: var(--primaryColor);
          color: #ffffff;
        }

        .enterprise-feature-card__icon-core svg {
          width: 28px;
          height: 28px;
        }

        .enterprise-feature-card__title {
          margin: 0 0 0.75rem;
          color: #1a1a2e;
          font-size: 1.35rem;
          font-weight: 800;
          letter-spacing: -0.01em;
        }

        .enterprise-feature-card__text {
          margin: 0;
          color: #64748b;
          font-size: 1rem;
          line-height: 1.7;
        }

        .enterprise-feature-card__accent {
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          height: 3px;
          background: var(--enterprise-accent);
          border-radius: 0 0 1.5rem 1.5rem;
          transform: scaleX(0);
          transform-origin: left;
          transition: transform 0.4s ease;
        }

        .enterprise-feature-card:hover .enterprise-feature-card__accent {
          transform: scaleX(1);
        }

        .enterprise-service-card {
          position: relative;
          min-height: 380px;
          border-radius: 1.5rem;
          overflow: hidden;
          background-size: cover;
          background-position: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
          transition:
            transform 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275),
            box-shadow 0.4s ease;
        }

        .enterprise-service-card--finance {
          background-image:
            linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)),
            url(${heroBanner});
        }

        .enterprise-service-card--accounting {
          background-image:
            linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)),
            url(${serviceBanner2});
        }

        .enterprise-service-card--crm {
          background-image:
            linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)),
            url(${serviceBanner3});
        }

        .enterprise-service-card--erp {
          background-image:
            linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)),
            url(${serviceBanner4});
        }

        .enterprise-service-card--tools {
          background-image:
            linear-gradient(to bottom, rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.8)),
            url(${serviceBanner5});
        }

        .enterprise-service-card:hover {
          transform: translateY(-10px);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
        }

        .enterprise-service-card__overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(244, 145, 30, 0.1) 0%, transparent 100%);
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .enterprise-service-card:hover .enterprise-service-card__overlay {
          opacity: 1;
        }

        .enterprise-service-card__icon {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          z-index: 2;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.15);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          color: #ffffff;
          font-size: 1.5rem;
          transition:
            transform 0.4s ease,
            background 0.4s ease,
            border-color 0.4s ease;
        }

        .enterprise-service-card:hover .enterprise-service-card__icon {
          transform: scale(1.1);
          background: rgba(244, 145, 30, 0.9);
          border-color: rgba(244, 145, 30, 0.9);
        }

        .enterprise-service-card__content {
          position: absolute;
          right: 0;
          bottom: 0;
          left: 0;
          z-index: 2;
          padding: 1.75rem 1.5rem;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0) 100%);
        }

        .enterprise-service-card__title {
          margin: 0 0 0.5rem;
          color: #ffffff;
          font-size: 1.5rem;
          font-weight: 700;
        }

        .enterprise-service-card__text {
          margin: 0;
          color: rgba(255, 255, 255, 0.7);
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .enterprise-pricing-card {
          position: relative;
          overflow: hidden;
          padding: 3rem;
          border-radius: 1.5rem;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.04);
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.06);
          transition:
            transform 0.4s ease,
            box-shadow 0.4s ease;
        }

        .enterprise-pricing-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
        }

        .enterprise-pricing-card__topline {
          position: absolute;
          top: 0;
          right: 0;
          left: 0;
          height: 4px;
          background: linear-gradient(90deg, var(--secondaryColor) 0%, #ff9f43 100%);
        }

        .enterprise-pricing-eyebrow {
          margin-bottom: 0.5rem;
          color: #94a3b8;
          font-size: 0.85rem;
          font-weight: 600;
          letter-spacing: 1px;
        }

        .enterprise-pricing-value {
          color: #1a1a2e;
          font-size: 3.5rem;
          font-weight: 800;
          line-height: 1;
          letter-spacing: -0.02em;
        }

        .enterprise-pricing-currency {
          color: #64748b;
          font-size: 1.5rem;
          font-weight: 400;
        }

        .enterprise-pricing-note {
          margin: 1rem 0 0;
          color: #64748b;
          font-size: 0.95rem;
          line-height: 1.6;
        }

        .enterprise-pricing-meta {
          height: 100%;
          padding: 1rem;
          border-radius: 1rem;
          background: #fafafa;
          border: 1px solid rgba(0, 0, 0, 0.04);
        }

        .enterprise-pricing-meta__label {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 0.5rem;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }

        .enterprise-pricing-meta__label--secondary {
          color: var(--secondaryColor);
        }

        .enterprise-pricing-meta__label--primary {
          color: var(--primaryColor);
        }

        .enterprise-pricing-meta__value {
          color: #111827;
          font-size: 1rem;
          font-weight: 700;
        }

        .enterprise-pricing-list-title {
          margin: 0 0 1rem;
          color: #94a3b8;
          font-size: 0.75rem;
          font-weight: 600;
          letter-spacing: 1px;
          text-transform: uppercase;
        }

        .enterprise-pricing-list {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .enterprise-pricing-list__item {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
        }

        .enterprise-pricing-list__icon {
          margin-top: 0.125rem;
          color: var(--secondaryColor);
          flex-shrink: 0;
        }

        .enterprise-pricing-list__item span {
          color: #475569;
          font-size: 0.95rem;
          line-height: 1.5;
        }

        .enterprise-project-card {
          height: 100%;
          border-radius: 1.5rem;
          overflow: hidden;
          background: #ffffff;
          border: 1px solid rgba(0, 0, 0, 0.04);
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
          transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
        }

        .enterprise-project-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
          border-color: rgba(244, 145, 30, 0.15);
        }

        .enterprise-project-card__media {
          position: relative;
          height: 280px;
          overflow: hidden;
          background-size: cover;
          background-repeat: no-repeat;
          background-position: center;
          transition: transform 0.6s ease;
        }

        .enterprise-project-media--hospital {
          background-image:
            linear-gradient(135deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.4) 100%),
            url(${serviceBanner5});
        }

        .enterprise-project-media--garage {
          background-image:
            linear-gradient(135deg, rgba(0, 0, 0, 0.2) 0%, rgba(0, 0, 0, 0.4) 100%),
            url(${projectBanner2});
        }

        .enterprise-project-card:hover .enterprise-project-card__media {
          transform: scale(1.05);
        }

        .enterprise-project-card__tag {
          position: absolute;
          top: 1.5rem;
          left: 1.5rem;
          z-index: 2;
          padding: 0.35rem 0.9rem;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(8px);
          color: var(--secondaryColor);
          font-size: 0.65rem;
          font-weight: 700;
          letter-spacing: 1.5px;
          text-transform: uppercase;
        }

        .enterprise-project-card__badge {
          position: absolute;
          right: 1.5rem;
          bottom: 1.5rem;
          z-index: 2;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 1rem;
          color: #ffffff;
          font-size: 1.6rem;
          box-shadow: 0 8px 24px rgba(244, 145, 30, 0.35);
          transition: transform 0.4s ease;
        }

        .enterprise-project-badge--healthcare {
          background: linear-gradient(135deg, #08aeea 0%, #2af598 100%);
        }

        .enterprise-project-badge--automotive {
          background: linear-gradient(135deg, var(--secondaryColor) 0%, #ff9f43 100%);
        }

        .enterprise-project-card:hover .enterprise-project-card__badge {
          transform: scale(1.1) rotate(-5deg);
        }

        .enterprise-project-card__media-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(135deg, rgba(244, 145, 30, 0.15) 0%, transparent 100%);
          opacity: 0;
          transition: opacity 0.4s ease;
        }

        .enterprise-project-card:hover .enterprise-project-card__media-overlay {
          opacity: 1;
        }

        .enterprise-project-card__content {
          padding: 3rem;
        }

        .enterprise-project-card__title {
          margin: 0 0 1rem;
          color: #1a1a2e;
          font-size: 1.5rem;
          font-weight: 800;
          letter-spacing: -0.01em;
        }

        .enterprise-project-card__description {
          margin: 0 0 1.5rem;
          color: #64748b;
          font-size: 1rem;
          line-height: 1.7;
        }

        .enterprise-project-feature {
          height: 100%;
          display: flex;
          align-items: center;
          gap: 0.625rem;
          padding: 0.75rem;
          border-radius: 1rem;
          background: rgba(244, 145, 30, 0.05);
          border: 1px solid rgba(244, 145, 30, 0.1);
        }

        .enterprise-project-feature__icon {
          color: var(--secondaryColor);
          font-size: 1rem;
        }

        .enterprise-project-feature__label {
          color: #475569;
          font-size: 0.85rem;
          font-weight: 500;
        }

        .enterprise-project-card__stats {
          display: flex;
          align-items: center;
          gap: 2rem;
          margin: 0 0 1.75rem;
          padding: 0 0 1.75rem;
          border-bottom: 1px solid rgba(0, 0, 0, 0.06);
        }

        .enterprise-project-card__stat {
          text-align: center;
        }

        .enterprise-project-card__stat-value {
          color: #1a1a2e;
          font-size: 1.4rem;
          font-weight: 700;
        }

        .enterprise-project-card__stat-label {
          color: #94a3b8;
          font-size: 0.75rem;
        }

        @keyframes enterprise-float {
          0%,
          100% {
            transform: translateY(0) translateX(0);
          }

          50% {
            transform: translateY(-25px) translateX(15px);
          }
        }

        @media (max-width: 991px) {
          .enterprise-section {
            padding: 4.5rem 0;
          }

          .enterprise-pricing-card {
            padding: 2.5rem;
          }
        }

        @media (max-width: 767px) {
          .enterprise-hero {
            min-height: auto;
            padding: 6rem 0 5rem;
            background-attachment: scroll;
          }

          .enterprise-section {
            min-height: auto;
            padding: 4rem 0;
          }

          .enterprise-section-heading {
            margin-bottom: 3rem;
          }

          .enterprise-feature-card,
          .enterprise-pricing-card,
          .enterprise-project-card__content {
            padding: 2rem;
          }

          .enterprise-service-card {
            min-height: 340px;
          }

          .enterprise-pricing-list {
            grid-template-columns: 1fr;
          }

          .enterprise-pricing-value {
            font-size: 2.5rem;
          }

          .enterprise-project-card__stats {
            gap: 1rem;
            justify-content: space-between;
          }
        }
      `}</style>
    </>
  );
}
